import {
    Button,
    Chip,
    ClickAwayListener,
    Collapse,
    Divider,
    Grow,
    IconButton,
    InputAdornment,
    List,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    TextField,
    Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { Eye, EyeOff, ListFilter, Search, X } from 'lucide-react';
import {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { asStructureSetId, RoiId } from '~/domain/changes/ids';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import {
    domainEventBus,
    RoiSelectionRequestedEvent,
    RoiVisibilityChangeRequestedEvent,
} from '~/domain/events';
import {
    RoiReadModel,
    StructureSetReadModel,
} from '~/domain/readmodels/StructureSetReadModel';
import { useRoiListView } from '~/hooks/domain/useRoiListView';
import { useStructureSetListView } from '~/hooks/domain/useStructureSetListView';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import {
    multiSelectedRoiNumbersState,
    selectedROIState,
    selectionAutoShownState,
} from '~/state/atoms/structureSet/roiAtoms';
import BulkActionsPanel from './BulkActionsPanel';
import {
    applyCtrlToggle,
    applyShiftRange,
    pruneSelection,
    reconcileActiveRoi,
} from './multiSelect';
import { getTypeSortGroup, sortRois } from './roiSort';
import StructureLayer from './StructureList/Structure';
import StructureDetails from './StructureList/Structure/StructureProperties/StructureDetails/StructureDetails';
import StructureSetDetails from './StructureSetHeader/StructureSetHeader';

export default function StructureSet({
    structureSet,
    renderExtraDetails,
    readOnly = false,
}: {
    structureSet: StructureSetReadModel;
    renderExtraDetails?: (
        roi: RoiReadModel,
        roiId: RoiId | undefined
    ) => ReactNode;
    readOnly?: boolean;
}) {
    const intl = useIntl();
    const itemRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
    const listRef = useRef<HTMLUListElement | null>(null);
    const selectedROINumber = useRecoilValue(selectedROIState);
    const setSelectedROINumber = useSetRecoilState(selectedROIState);
    const setAutoShown = useSetRecoilState(selectionAutoShownState);
    const [multiSelected, setMultiSelected] = useRecoilState(
        multiSelectedRoiNumbersState
    );
    // Last non-shift click; the origin for a shift-range. Cleared on set change.
    const anchorRef = useRef<number | null>(null);
    const { createRoi } = useStructureSet();

    // Projection hooks — resolve the branded StructureSetId by matching dicomUid,
    // then get the list view. These run unconditionally to satisfy hook rules.
    const structureSetItems = useStructureSetListView();
    const listItem = useMemo(
        () =>
            structureSetItems.find(
                (i) => i.dicomUid === structureSet.dicomUid
            ) ?? null,
        [structureSetItems, structureSet.dicomUid]
    );
    // Falls back to empty array when ssId is not yet resolved.
    // asStructureSetId('') won't match any real UUID so useRoiListView returns [].
    const roiListView = useRoiListView(listItem?.id ?? asStructureSetId(''));

    /**
     * Map from roiNumber → RoiId (from the projection). Used to pass branded
     * RoiId to row components so they can call useRoiCommands(). roiNumber is
     * the only ROI identifier DICOM requires to be unique within a structure
     * set — ROIName is free text and imported sets can carry duplicates, so
     * keying by name would route commands to the wrong structure.
     */
    const roiIdByNumber = useMemo((): ReadonlyMap<number, RoiId> => {
        const map = new Map<number, RoiId>();
        for (const item of roiListView) {
            map.set(item.roiNumber, item.id);
        }
        return map;
    }, [roiListView]);
    const [createMenuOpen, setCreateMenuOpen] = useState(false);
    const createButtonRef = useRef<HTMLDivElement>(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTypes, setSelectedTypes] = useState<Set<RoiInterpretedType>>(
        new Set()
    );
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);

    const handleCloseCreateMenu = (event: Event) => {
        if (
            createButtonRef.current &&
            createButtonRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }
        setCreateMenuOpen(false);
    };

    const selectedStructure = structureSet?.roiArray?.find(
        (roi) => roi?.roiNumber === selectedROINumber
    );

    const sortedRois = useMemo(() => {
        if (!structureSet?.roiArray) {
            return [];
        }
        return [...structureSet.roiArray].sort(sortRois);
    }, [structureSet]);

    const presentTypes = useMemo(() => {
        const types = new Set<RoiInterpretedType>();
        for (const roi of sortedRois) {
            if (roi.interpretedType) {
                types.add(roi.interpretedType);
            }
        }
        return Array.from(types).sort(
            (a, b) => getTypeSortGroup(a) - getTypeSortGroup(b)
        );
    }, [sortedRois]);

    // Remove stale type filters when structures are deleted
    useEffect(() => {
        setSelectedTypes((prev) => {
            const presentSet = new Set(presentTypes);
            const stale = [...prev].some((t) => !presentSet.has(t));
            if (!stale) {
                return prev;
            }
            const next = new Set([...prev].filter((t) => presentSet.has(t)));
            return next;
        });
    }, [presentTypes]);

    const filteredRois = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        return sortedRois.filter((roi) => {
            if (query && !roi.name.toLowerCase().includes(query)) {
                return false;
            }
            if (
                selectedTypes.size > 0 &&
                !selectedTypes.has(roi.interpretedType)
            ) {
                return false;
            }
            return true;
        });
    }, [sortedRois, searchQuery, selectedTypes]);

    // Clear bulk selection on structure-set switch: ROI numbers are reused
    // across sets, so read-time pruning alone could rebind a stale entry.
    useEffect(() => {
        anchorRef.current = null;
        setMultiSelected(new Set());
    }, [structureSet.dicomUid, setMultiSelected]);

    // Drop selected members that no longer exist; same-reference when stable.
    useEffect(() => {
        const existing = new Set(
            structureSet.roiArray.map((roi) => roi.roiNumber)
        );
        setMultiSelected((prev) => pruneSelection(prev, existing));
    }, [structureSet, setMultiSelected]);

    const clearMultiSelection = useCallback(() => {
        setMultiSelected(new Set());
        anchorRef.current = null;
    }, [setMultiSelected]);

    // Keep the active ROI a member of a non-empty bulk set so its row highlight
    // can't disagree with the selection chips. A direct atom write moves only
    // the highlight — no auto-show or toggle-off, matching how members are
    // otherwise selected in bulk mode.
    const reconcileActiveSelection = useCallback(
        (nextSet: ReadonlySet<number>, clicked: number) => {
            const order = filteredRois.map((roi) => roi.roiNumber);
            const target = reconcileActiveRoi(
                nextSet,
                clicked,
                selectedROINumber,
                order
            );
            if (target !== selectedROINumber) {
                setSelectedROINumber(target);
                setAutoShown(false);
            }
        },
        [filteredRois, selectedROINumber, setSelectedROINumber, setAutoShown]
    );

    const handleRowSelectionInput = useCallback(
        (roiNumber: number, mods: { ctrl: boolean; shift: boolean }) => {
            const order = filteredRois.map((roi) => roi.roiNumber);

            if (mods.shift) {
                const anchor =
                    anchorRef.current ??
                    (selectedROINumber !== -1 &&
                    order.includes(selectedROINumber)
                        ? selectedROINumber
                        : null);
                if (anchor === null) {
                    return false;
                }
                const next = applyShiftRange(order, anchor, roiNumber);
                if (next.size < 2) {
                    return false;
                }
                setMultiSelected(next);
                reconcileActiveSelection(next, roiNumber);
                return true;
            }

            if (mods.ctrl) {
                const next = applyCtrlToggle(
                    multiSelected,
                    selectedROINumber,
                    roiNumber
                );
                anchorRef.current = roiNumber;
                if (next.size < 2) {
                    setMultiSelected(new Set());
                    return false;
                }
                setMultiSelected(next);
                reconcileActiveSelection(next, roiNumber);
                return true;
            }

            if (multiSelected.size > 0) {
                setMultiSelected(new Set());
                anchorRef.current = roiNumber;
                // Exiting bulk mode by clicking the active row keeps it
                // selected instead of falling through to toggle-deselect.
                return roiNumber === selectedROINumber;
            }
            anchorRef.current = roiNumber;
            return false;
        },
        [
            filteredRois,
            multiSelected,
            selectedROINumber,
            setMultiSelected,
            reconcileActiveSelection,
        ]
    );

    const hiddenSelectedRoiNumbers = useMemo(() => {
        const visibleNumbers = new Set(
            filteredRois.map((roi) => roi.roiNumber)
        );
        const hidden = new Set<number>();
        for (const roiNumber of multiSelected) {
            if (!visibleNumbers.has(roiNumber)) {
                hidden.add(roiNumber);
            }
        }
        return hidden;
    }, [filteredRois, multiSelected]);

    const handleRemoveFromSelection = useCallback(
        async (roiNumber: number) => {
            const next = new Set(multiSelected);
            next.delete(roiNumber);
            if (next.size >= 2) {
                setMultiSelected(next);
                // Removing the active member's chip would orphan its highlight;
                // move the active ROI to a survivor.
                reconcileActiveSelection(next, roiNumber);
                return;
            }
            setMultiSelected(new Set());
            anchorRef.current = null;
            // Hand the survivor back to single-selection; re-selecting the
            // active ROI would toggle it off, so skip when already active.
            const survivor: number | undefined = next.values().next().value;
            if (survivor !== undefined && survivor !== selectedROINumber) {
                await domainEventBus.publish(
                    new RoiSelectionRequestedEvent(survivor, 'user')
                );
            }
        },
        [
            multiSelected,
            selectedROINumber,
            setMultiSelected,
            reconcileActiveSelection,
        ]
    );

    const allVisible = useMemo(
        () =>
            filteredRois.length > 0 && filteredRois.every((roi) => roi.visible),
        [filteredRois]
    );

    const handleToggleAllVisibility = useCallback(async () => {
        const newVisibility = !allVisible;
        // Hide-all skips the selected ROI so focus isn't yanked away.
        const targets = newVisibility
            ? filteredRois
            : filteredRois.filter((roi) => roi.roiNumber !== selectedROINumber);
        await Promise.all(
            targets.map((roi) =>
                domainEventBus.publish(
                    new RoiVisibilityChangeRequestedEvent(
                        roi.roiNumber,
                        newVisibility,
                        'user'
                    )
                )
            )
        );
        // Hide-all → selected is now solo-ephemeral; Show-all → no longer.
        setAutoShown(!newVisibility);
    }, [filteredRois, allVisible, selectedROINumber, setAutoShown]);

    const handleToggleType = (type: RoiInterpretedType) => {
        setSelectedTypes((prev) => {
            const next = new Set(prev);
            if (next.has(type)) {
                next.delete(type);
            } else {
                next.add(type);
            }
            return next;
        });
    };

    useEffect(() => {
        const item = itemRefs.current[selectedROINumber];
        const list = listRef.current;
        if (selectedROINumber && item && list) {
            const itemRect = item.getBoundingClientRect();
            const listRect = list.getBoundingClientRect();
            const isAboveView = itemRect.top < listRect.top;
            const isBelowView = itemRect.bottom > listRect.bottom;
            if (isAboveView || isBelowView) {
                item.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth',
                });
            }
        }
    }, [selectedROINumber]);

    const assignItemRef = (roiNumber: number, el: HTMLDivElement | null) => {
        itemRefs.current[roiNumber] = el;
    };

    return (
        <Box
            position="relative"
            display="grid"
            gridTemplateRows={'auto 1fr auto'}
        >
            <Box>
                <StructureSetDetails structureSet={structureSet} />
                <Box px={1.5}>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        ml={-0.25}
                        mt={1}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            placeholder="Search structures..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment
                                        position="start"
                                        sx={{ ml: -0.5 }}
                                    >
                                        <Search size={10} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery ? (
                                    <InputAdornment
                                        position="end"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setSearchQuery('')}
                                    >
                                        <X size={10} />
                                    </InputAdornment>
                                ) : null,
                            }}
                            sx={{
                                '& .MuiInputBase-input': {
                                    fontSize: 11,
                                    py: 0.5,
                                },
                            }}
                        />
                        <IconButton
                            size="small"
                            onClick={() => setFilterPanelOpen((prev) => !prev)}
                            aria-label={intl.formatMessage({
                                id: 'toggleTypeFilters',
                            })}
                            sx={{
                                mr: -0.5,
                                color:
                                    filterPanelOpen || selectedTypes.size > 0
                                        ? 'primary.main'
                                        : 'text.secondary',
                            }}
                        >
                            <ListFilter size={12} />
                        </IconButton>
                    </Box>
                    <Collapse in={filterPanelOpen}>
                        {presentTypes.length > 1 && (
                            <Box
                                display="flex"
                                gap={0.5}
                                mt={0.75}
                                mx={-0.25}
                                flexWrap="wrap"
                            >
                                {presentTypes.map((type) => (
                                    <Chip
                                        key={type}
                                        label={type}
                                        size="small"
                                        variant={
                                            selectedTypes.has(type)
                                                ? 'filled'
                                                : 'outlined'
                                        }
                                        onClick={() => handleToggleType(type)}
                                        sx={{
                                            fontSize: 9,
                                            px: 0,
                                            height: 18,
                                            maxWidth: 'none',
                                            borderColor: selectedTypes.has(type)
                                                ? 'primary.main'
                                                : 'divider',
                                            bgcolor: selectedTypes.has(type)
                                                ? 'primary.main'
                                                : 'transparent',
                                            color: selectedTypes.has(type)
                                                ? 'primary.contrastText'
                                                : 'text.secondary',
                                            '&:hover': {
                                                bgcolor: selectedTypes.has(type)
                                                    ? 'primary.dark'
                                                    : 'action.hover',
                                            },
                                        }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Collapse>
                    <Box display="flex" alignItems="center" mt={1}>
                        <IconButton
                            size="small"
                            onClick={handleToggleAllVisibility}
                            aria-label={
                                allVisible
                                    ? 'toggle-all-visibility-eye'
                                    : 'toggle-all-visibility-eyeoff'
                            }
                            data-testid="toggle-all-visibility"
                            sx={{
                                mr: 0.5,
                                ml: -0.75,
                                color: 'primary.main',
                            }}
                        >
                            {allVisible ? (
                                <Eye size={12} />
                            ) : (
                                <EyeOff size={12} />
                            )}
                        </IconButton>
                        <Typography
                            sx={{
                                fontSize: 12,
                                color: 'text.secondary',
                                fontWeight: 500,
                                flex: 1,
                            }}
                        >
                            {`Structures ${filteredRois.length}`}
                        </Typography>
                        {!readOnly && (
                            <Box position="relative" ref={createButtonRef}>
                                <Button
                                    variant="text"
                                    color="primary"
                                    onClick={() =>
                                        setCreateMenuOpen((prev) => !prev)
                                    }
                                    disabled={structureSet.isApproved}
                                    aria-controls={
                                        createMenuOpen
                                            ? 'create-menu'
                                            : undefined
                                    }
                                    aria-haspopup="menu"
                                    sx={{
                                        fontSize: 11,
                                        py: 0,
                                        px: 0,
                                        minHeight: 0,
                                        minWidth: 0,
                                        textTransform: 'none',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                    data-testid="create-roi-button"
                                >
                                    + New
                                </Button>
                                <Popper
                                    sx={{ zIndex: 1 }}
                                    open={createMenuOpen}
                                    anchorEl={createButtonRef.current}
                                    role={undefined}
                                    transition
                                    disablePortal
                                    placement="bottom-end"
                                >
                                    {({ TransitionProps, placement }) => (
                                        <Grow
                                            {...TransitionProps}
                                            style={{
                                                transformOrigin:
                                                    placement === 'bottom-end'
                                                        ? 'right top'
                                                        : 'right bottom',
                                            }}
                                        >
                                            <Paper
                                                elevation={4}
                                                sx={{
                                                    mt: 0.5,
                                                    border: 1,
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                <ClickAwayListener
                                                    onClickAway={
                                                        handleCloseCreateMenu
                                                    }
                                                >
                                                    <MenuList
                                                        id="create-menu"
                                                        autoFocusItem
                                                        dense
                                                    >
                                                        <MenuItem
                                                            onClick={() => {
                                                                createRoi();
                                                                setCreateMenuOpen(
                                                                    false
                                                                );
                                                            }}
                                                            disabled={
                                                                structureSet.isApproved
                                                            }
                                                        >
                                                            Structure
                                                        </MenuItem>
                                                        <MenuItem
                                                            onClick={() => {
                                                                createRoi(
                                                                    RoiInterpretedType.MARKER
                                                                );
                                                                setCreateMenuOpen(
                                                                    false
                                                                );
                                                            }}
                                                            disabled={
                                                                structureSet.isApproved
                                                            }
                                                        >
                                                            Point
                                                        </MenuItem>
                                                    </MenuList>
                                                </ClickAwayListener>
                                            </Paper>
                                        </Grow>
                                    )}
                                </Popper>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
            <Box position="relative" height="100%">
                <Box position="absolute" width="100%" height="100%">
                    <List
                        ref={listRef}
                        data-structure-list
                        role="listbox"
                        aria-multiselectable="true"
                        aria-label={intl.formatMessage({
                            id: 'structure.bulk.listAriaLabel',
                        })}
                        sx={{
                            position: 'static',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            height: '100%',
                            '& ul': { padding: 0 },
                        }}
                    >
                        {filteredRois.map((roi) => (
                            <div
                                key={`${structureSet.dicomUid}-${roi.roiNumber}`}
                                ref={(el) => assignItemRef(roi.roiNumber, el)}
                            >
                                <StructureLayer
                                    roi={roi}
                                    roiId={roiIdByNumber.get(roi.roiNumber)}
                                    isMultiSelected={multiSelected.has(
                                        roi.roiNumber
                                    )}
                                    onSelectionInput={handleRowSelectionInput}
                                />
                            </div>
                        ))}
                    </List>
                </Box>
            </Box>
            {multiSelected.size >= 2 ? (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                >
                    <Divider />
                    <Box px={1.5} py={1.5}>
                        <BulkActionsPanel
                            structureSet={structureSet}
                            selectedRoiNumbers={multiSelected}
                            hiddenSelectedRoiNumbers={hiddenSelectedRoiNumbers}
                            onClearSelection={clearMultiSelection}
                            onRemoveFromSelection={handleRemoveFromSelection}
                        />
                    </Box>
                </Box>
            ) : selectedStructure ? (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                >
                    <Divider />
                    <Box px={1.5} py={1.5}>
                        <StructureDetails
                            roi={selectedStructure}
                            roiId={roiIdByNumber.get(
                                selectedStructure.roiNumber
                            )}
                        />
                        {renderExtraDetails?.(
                            selectedStructure,
                            roiIdByNumber.get(selectedStructure.roiNumber)
                        )}
                    </Box>
                </Box>
            ) : null}
        </Box>
    );
}
